import {
  createTheme,
  MantineColorsTuple,
  MantineProvider,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import * as React from "react";
import * as ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import App from "./App";
import "./index.css";

// core styles are required for all packages
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/carousel/styles.css";
import '@mantine/tiptap/styles.css';

import SignInPage from "./components/auth/SignInPage";
import Loader from "./components/loader/Loader";
import Settings from "./components/settings/Settings";
import RemoteControl from "./components/remote/RemoteControl";
import RemoteControlled from "./components/remote/RemoteControlled";

// other css files are required only if
// you are using components from the corresponding package
// import '@mantine/dates/styles.css';
import "@mantine/dropzone/styles.css";
import Account from "./components/account/Account";
import Admin from "./components/admin/Admin";
import ErrorPage from "./components/error/ErrorPage";
import MapView, { MapViewMode } from "./components/mapView/MapView";
import Resolver from "./components/resolver/Resolver";
import GeneralGallery from "./components/generalGallery/GeneralGallery";
// import '@mantine/code-highlight/styles.css';
// ...

import { store } from "./app/store";
import { Provider } from "react-redux";
import { createRef } from "react";
import BottomDrawer from "./components/bottomSheet/BottomDrawer";
import Onboarding from "./components/onboarding/Onboarding";

import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import SearchView from "./components/search/SearchView";
import HomeView from "./components/home/HomeView";
import Timeline from "./components/timeline/Timeline";
import AlbumsView from "./components/albums/AlbumsView";
import JpView from "./components/jp/JpView";
import AlbumDetail from "./components/albums/AlbumDetail";
polyfillCountryFlagEmojis();

const myColor: MantineColorsTuple = [
  "#e7f9ff",
  "#d5effa",
  "#abddf2",
  "#7ecbea",
  "#5bbce4",
  "#45b2e0",
  "#38addf",
  "#2898c7",
  "#1787b2",
  "#00759e",
];

const theme = createTheme({
  /** Put your mantine theme override here */
  colors: { myColor },
  white: "#f5f5f5",
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

export const routes = [
  {
    path: "/",
    element: <Navigate to="/browse" replace />,
  },
  // TODO: remove
  {
    path: "jp",
    element: <JpView />,
    nodeRef: createRef(),
  },
  {
    path: "/remote",
    element: <RemoteControl />,
    nodeRef: createRef(),
  },
  {
    path: "/remote-control",
    element: <RemoteControlled />,
    nodeRef: createRef(),
  },
  {
    path: "/demo",
    element: <App demo />,
    children: [
      {
        path: "settings",
        element: <Settings />,
        nodeRef: createRef(),
      },
      {
        path: "browse",
        element: (
          <Resolver waitFor="profile">
            <Loader demo />
          </Resolver>
        ),
        children: [
          {
            path: ":folder/*",
            //element: <Gallery />,
            // loader: folderLoader,
          },
        ],
        nodeRef: createRef(),
      },
      {
        path: "account",
        element: (
          <Resolver waitFor="profile">
            <Account demo />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "gallery",
        element: (
          <Resolver waitFor="profile">
            <GeneralGallery demo />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "search",
        element: (
          <Resolver waitFor="profile">
            <SearchView />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "timeline",
        element: (
          <Resolver waitFor="profile">
            <Timeline demo />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
    ],
  },
  {
    path: "/onboarding",
    element: <Onboarding />,
    nodeRef: createRef(),
  },
  {
    path: "/",
    element: <App />,
    //errorElement: <ErrorPage />,
    children: [
      {
        path: "settings",
        element: <Settings />,
        nodeRef: createRef(),
      },
      {
        path: "account",
        element: (
          <Resolver waitFor="profile">
            <Account />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "gallery",
        element: (
          <Resolver waitFor="profile">
            <GeneralGallery />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "search",
        element: (
          <Resolver waitFor="profile">
            <SearchView />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "map",
        element: <MapView defaultMode={MapViewMode.VIEW} />,
        nodeRef: createRef(),
      },
      {
        path: "timeline",
        element: (
          <Resolver waitFor="profile">
            <Timeline />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "home",
        element: (
          <Resolver waitFor="profile">
            <HomeView />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "albums",
        element: (
          <Resolver waitFor="profile">
            <AlbumsView />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "albums/:id",
        element: (
          <Resolver waitFor="profile">
            <AlbumDetail />
          </Resolver>
        ),
        nodeRef: createRef(),
      },
      {
        path: "browse",
        element: (
          <Resolver waitFor="profile">
            <Loader />
          </Resolver>
        ),
        children: [
          {
            path: ":folder/*",
            //element: <Gallery />,
            // loader: folderLoader,
          },
        ],
        nodeRef: createRef(),
      },
    ],
  },
  {
    path: "/login",
    element: <SignInPage />,
    nodeRef: createRef(),
  },
  {
    path: "*",
    element: <ErrorPage />,
    nodeRef: createRef(),
  },
];

const router = createBrowserRouter(routes);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <MantineProvider theme={theme} defaultColorScheme="dark">
        <Notifications />
        <ModalsProvider>
          <RouterProvider router={router} />
        </ModalsProvider>
      </MantineProvider>
    </Provider>
  </React.StrictMode>
);
